import AdminPermission from "components/admin/AdminPermission";
import { FulfilmentTask, gamificationApi } from "api/admin/gamification";
import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
  Button,
  Drawer,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  SectionCard,
  StatusPill,
  errorMessage,
  fieldClass,
  formatDate,
  formatLabel,
} from "./components";

export default function Fulfilment() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<FulfilmentTask["status"] | "">("pending");
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<FulfilmentTask | undefined>();
  const [note, setNote] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [taskStatus, setTaskStatus] = useState<"pending" | "in_progress" | "cancelled">("pending");
  const limit = 25;
  const tasks = useQuery(
    ["gamification-fulfilment", status, offset],
    () => gamificationApi.listFulfilmentTasks({ status, limit, offset }),
    { retry: false, keepPreviousData: true },
  );
  const complete = useMutation(
    ({ taskId, completionNote }: { taskId: number; completionNote: string }) =>
      gamificationApi.completeFulfilmentTask(taskId, completionNote),
    {
      onSuccess: () => {
        toast.success("Reward fulfilment completed");
        queryClient.invalidateQueries("gamification-fulfilment");
        queryClient.invalidateQueries("gamification-fulfilment-overview");
        setSelected(undefined);
        setNote("");
      },
      onError: (error) => {
        toast.error(errorMessage(error));
      },
    },
  );
  const update = useMutation(
    ({ taskId, input }: { taskId: number; input: { status?: "pending" | "in_progress" | "cancelled"; dueAt?: string | null; assignToSelf?: boolean } }) =>
      gamificationApi.updateFulfilmentTask(taskId, input),
    {
      onSuccess: () => {
        toast.success("Fulfilment task updated");
        queryClient.invalidateQueries("gamification-fulfilment");
        setSelected(undefined);
      },
      onError: (error) => {
        toast.error(errorMessage(error));
      },
    },
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (selected) complete.mutate({ taskId: selected.id, completionNote: note });
  };

  return (
    <>
      <SectionCard
        title="Pending rewards"
        description="Complete rewards that need an admin to deliver them."
        action={
          <select
            aria-label="Filter pending rewards by status"
            className="rounded-lg border-gray-300 text-sm focus:border-orange-400 focus:ring-orange-400"
            value={status}
            onChange={(event) => {
              setOffset(0);
              setStatus(event.target.value as FulfilmentTask["status"] | "");
            }}
          >
            <option value="">All tasks</option>
            {["pending", "in_progress", "completed", "cancelled"].map((value) => (
              <option key={value} value={value}>{formatLabel(value)}</option>
            ))}
          </select>
        }
      >
        {tasks.isLoading ? (
          <LoadingState rows={5} />
        ) : tasks.error ? (
          <ErrorState error={tasks.error} onRetry={() => tasks.refetch()} />
        ) : !tasks.data?.tasks.length ? (
          <EmptyState
            title="Nothing needs attention"
            body="There are no rewards with this status."
            icon="fa-circle-check"
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100 text-left">
                <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-5 py-3">Reward task</th>
                    <th className="px-5 py-3">Reward record</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Assignee</th>
                    <th className="px-5 py-3">Created</th>
                    <th className="px-5 py-3">Completion note</th>
                    <th className="px-5 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {tasks.data.tasks.map((task) => (
                    <tr key={task.id}>
                      <td className="px-5 py-4 font-semibold text-gray-950">
                        #{task.id}
                        {task.dueAt && (
                          <p className="mt-0.5 text-xs font-normal text-gray-400">
                            Due {formatDate(task.dueAt)}
                          </p>
                        )}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-600">
                        #{task.grantId}
                      </td>
                      <td className="px-5 py-4">
                        <StatusPill status={task.status} />
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-600">
                        {task.assignedAdminId ? `Admin #${task.assignedAdminId}` : "Unassigned"}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-sm text-gray-600">
                        {formatDate(task.createdAt)}
                      </td>
                      <td className="max-w-[280px] px-5 py-4 text-sm text-gray-600">
                        <p className="truncate">
                          {task.completionNote ||
                            (task.metadata
                              ? JSON.stringify(task.metadata)
                              : "No metadata")}
                        </p>
                      </td>
                      <td className="px-5 py-4 text-right">
                        {task.status !== "completed" &&
                        task.status !== "cancelled" ? (
                          <AdminPermission
                            permission={["gamification.fulfilment.edit", "gamification.fulfilment.approve"]}
                          >
                            <Button
                              variant="secondary"
                              onClick={() => {
                                setSelected(task);
                                setNote("");
                                setDueAt(task.dueAt ? task.dueAt.slice(0, 16) : "");
                                setTaskStatus(task.status === "in_progress" ? "in_progress" : "pending");
                              }}
                            >
                              Manage
                            </Button>
                          </AdminPermission>
                        ) : (
                          <span className="text-xs text-gray-400">
                            {task.completedAt
                              ? formatDate(task.completedAt)
                              : "Closed"}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t border-gray-100 px-5 py-4">
              <p className="text-sm text-gray-500">
                {tasks.data.total} task{tasks.data.total === 1 ? "" : "s"}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  disabled={offset === 0}
                  onClick={() => setOffset((value) => Math.max(0, value - limit))}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  disabled={offset + limit >= tasks.data.total}
                  onClick={() => setOffset((value) => value + limit)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </SectionCard>

      <Drawer
        open={Boolean(selected)}
        title={`Manage reward task #${selected?.id ?? ""}`}
        description="Assign it to yourself, set a due date, and record the final delivery."
        onClose={() => setSelected(undefined)}
      >
        <form className="space-y-5" onSubmit={submit}>
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm">
            <p className="font-semibold text-gray-900">
              Reward record #{selected?.grantId}
            </p>
            <p className="mt-1 text-gray-500">
              Completing this task also marks the reward as delivered.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Work status">
              <select className={fieldClass} value={taskStatus} onChange={(event) => setTaskStatus(event.target.value as "pending" | "in_progress" | "cancelled")}>
                <option value="pending">Pending</option>
                <option value="in_progress">In progress</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </Field>
            <Field label="Due date">
              <input type="datetime-local" className={fieldClass} value={dueAt} onChange={(event) => setDueAt(event.target.value)} />
            </Field>
          </div>
          <div className="flex justify-end">
            <AdminPermission permission={"gamification.fulfilment.edit"}>
              <Button
                type="button"
                variant="secondary"
                disabled={update.isLoading}
                onClick={() =>
                  selected &&
                  update.mutate({
                    taskId: selected.id,
                    input: {
                      status: taskStatus,
                      dueAt: dueAt ? new Date(dueAt).toISOString() : null,
                      assignToSelf: true,
                    },
                  })
                }
              >
                {update.isLoading ? "Updating…" : "Assign to me and update"}
              </Button>
            </AdminPermission>
          </div>
          <Field label="What was delivered?" required hint="Add a reference or short delivery note.">
            <textarea
              required
              minLength={2}
              rows={5}
              className={fieldClass}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </Field>
          <div className="flex justify-end gap-3 border-t border-gray-100 pt-5">
            <Button type="button" variant="secondary" onClick={() => setSelected(undefined)}>
              Cancel
            </Button>
            <AdminPermission permission={"gamification.fulfilment.approve"}>
              <Button disabled={complete.isLoading || taskStatus === "cancelled"} type="submit">
                {complete.isLoading ? "Completing…" : "Confirm completion"}
              </Button>
            </AdminPermission>
          </div>
        </form>
      </Drawer>
    </>
  );
}
